<script lang="ts">
    import * as Sentry from '@sentry/browser';
    import { createEventDispatcher } from 'svelte';

    import { trans } from '$assets/translator.js';
    import DownloadIcon from '$backoffice/icons/Download.svelte';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';

    import FocalPointEditor from './FocalPointEditor.svelte';
    import ImageCropEditor from './ImageCropEditor.svelte';

    export let apiEndpoint: string;
    export let mediaBaseUrl: string;
    export let cropProxyUrl: string;

    let isHovering: boolean = false;
    let isUploading: boolean = false;
    let uploadError: string | null = null;
    const dispatch = createEventDispatcher<{
        fileSelected: {
            file: File | null;
            url: string | null;
            error?: string | null;
            focalPoint?: { x: number; y: number } | null;
            imageWidth?: number | null;
            imageHeight?: number | null;
        };
    }>();

    let fileInput: HTMLInputElement;
    let pendingFile: File | null = null;
    let pendingCroppedFile: File | null = null;
    let pendingCroppedBlobUrl: string = '';
    let pendingFocalPoint: { x: number; y: number } | null = null;

    function handleCropConfirmed(event: CustomEvent<{ file: File }>) {
        pendingCroppedFile = event.detail.file;
        pendingCroppedBlobUrl = URL.createObjectURL(pendingCroppedFile);
    }

    function handleCropCancelled() {
        pendingFile = null;
    }

    function handleFocalPointConfirmed(
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) {
        pendingFocalPoint = event.detail.focalPoint;
        const file = pendingCroppedFile!;
        URL.revokeObjectURL(pendingCroppedBlobUrl);
        pendingCroppedBlobUrl = '';
        pendingCroppedFile = null;
        pendingFile = null;
        handleFileSelect(file);
    }

    function handleFocalPointCancelled() {
        URL.revokeObjectURL(pendingCroppedBlobUrl);
        pendingCroppedBlobUrl = '';
        pendingCroppedFile = null;
        // pendingFile still set → ImageCropper re-appears
    }

    async function postFileAndData(
        file: File,
    ): Promise<{ path: string; width: number; height: number }> {
        const formData = new FormData();
        formData.append('content', file);
        const filenameWithoutExtension = file.name.includes('.')
            ? file.name.split('.').slice(0, -1).join('.')
            : file.name;
        formData.append('name', filenameWithoutExtension);

        try {
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json, application/ld+json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                let detail = `Échec POST: ${
                    response.statusText || response.status
                }`;
                try {
                    const errorJson = JSON.parse(errorText);
                    detail = errorJson.detail || errorJson.title || detail;
                } catch {
                    /* Ignorer si pas JSON */
                }
                const errorToReport = new Error(detail);
                Sentry.captureException(errorToReport);
                throw errorToReport;
            }

            const postData = await response.json();
            const imagePath = postData.path;
            const imageWidth = Number(postData.originalWidth ?? postData.width);
            const imageHeight = Number(
                postData.originalHeight ?? postData.height,
            );

            if (!imagePath) {
                console.error('POST Response missing path (or url):', postData);
                throw new Error(
                    'Réponse API (Proxy/POST) invalide: path/url manquant.',
                );
            }

            if (!Number.isFinite(imageWidth) || !Number.isFinite(imageHeight)) {
                console.error('POST Response missing dimensions:', postData);
                throw new Error(
                    'Réponse API (Proxy/POST) invalide: dimensions manquantes.',
                );
            }

            console.debug(
                'POST successful, received path directly:',
                imagePath,
            );

            return {
                path: imagePath,
                width: imageWidth,
                height: imageHeight,
            };
        } catch (error) {
            console.error('Error during POST request:', error);
            Sentry.captureException(error);
            throw error;
        }
    }

    async function handleFileSelect(file: File): Promise<void> {
        if (!file.type.startsWith('image/')) {
            uploadError = 'Type de fichier invalide.';
            return;
        }

        uploadError = null;
        isUploading = true;
        let previewBlobUrl: string | null = null;
        let currentUploadError: string | null = null;

        try {
            previewBlobUrl = URL.createObjectURL(file);
            dispatch('fileSelected', {
                file,
                url: previewBlobUrl,
                error: null,
                focalPoint: pendingFocalPoint,
                imageWidth: null,
                imageHeight: null,
            });

            const uploadedImage = await postFileAndData(file);

            if (!uploadedImage.path || typeof uploadedImage.path !== 'string') {
                throw new Error('Chemin invalide reçu après upload.');
            }

            let finalUrl: string;
            try {
                const cleanImagePath = uploadedImage.path.startsWith('/')
                    ? uploadedImage.path.substring(1)
                    : uploadedImage.path;
                finalUrl = `${mediaBaseUrl}/assets/${cleanImagePath}`;
            } catch (e) {
                console.error(
                    'Error constructing final URL with base:',
                    mediaBaseUrl,
                    'and path:',
                    uploadedImage.path,
                    e,
                );
                const constructError = new Error(
                    "Impossible de construire l'URL finale de l'image.",
                );
                Sentry.captureException(constructError, {
                    extra: { mediaBaseUrl, imagePath: uploadedImage.path },
                });
                throw constructError;
            }

            dispatch('fileSelected', {
                file,
                url: finalUrl,
                error: null,
                focalPoint: pendingFocalPoint,
                imageWidth: uploadedImage.width,
                imageHeight: uploadedImage.height,
            });
        } catch (error) {
            console.error('Error during overall file processing:', error);
            currentUploadError =
                error instanceof Error ? error.message : 'Échec du traitement.';
            uploadError = currentUploadError;

            if (
                !(
                    error instanceof Error &&
                    error.message.startsWith('Échec POST:')
                )
            ) {
                Sentry.captureException(error);
            }

            if (previewBlobUrl) {
                dispatch('fileSelected', {
                    file: null,
                    url: null,
                    error: currentUploadError,
                });
                previewBlobUrl = null;
            }
        } finally {
            isUploading = false;
        }
    }

    function handleDragOver(event: DragEvent): void {
        event.preventDefault();
        isHovering = true;
    }
    function handleDragLeave(): void {
        isHovering = false;
    }
    function handleDrop(event: DragEvent): void {
        event.preventDefault();
        isHovering = false;
        uploadError = null;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            pendingFile = event.dataTransfer.files[0];
        }
    }
    function handleFileInputChange(event: Event): void {
        uploadError = null;
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            pendingFile = target.files[0];
            target.value = '';
        }
    }
    function triggerFileInput(): void {
        uploadError = null;
        fileInput.click();
    }
    function handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileInput();
        }
    }
</script>

{#if pendingCroppedFile && pendingCroppedBlobUrl}
    <FocalPointEditor
        src={pendingCroppedBlobUrl}
        initialFocalPoint={{ x: 50, y: 50 }}
        on:confirm={handleFocalPointConfirmed}
        on:cancel={handleFocalPointCancelled}
    />
{:else if pendingFile}
    <ImageCropEditor
        file={pendingFile}
        {cropProxyUrl}
        on:cropped={handleCropConfirmed}
        on:cancel={handleCropCancelled}
    />
{/if}

<div class="relative">
    <input
        type="file"
        bind:this={fileInput}
        accept="image/*"
        on:change={handleFileInputChange}
        class="hidden"
    />
    <div
        role="button"
        tabindex={isUploading ? -1 : 0}
        class={cn(
            'rounded-lg border-2 border-dashed p-xl text-center transition-colors',
            'border-border-quiet',
            isHovering &&
                !isUploading &&
                'border-content-quiet bg-container-quiet',
            isUploading
                ? 'bg-container-lowest cursor-not-allowed opacity-70'
                : 'cursor-pointer',
        )}
        on:dragover|preventDefault={isUploading ? undefined : handleDragOver}
        on:dragleave={isUploading ? undefined : handleDragLeave}
        on:drop|preventDefault={isUploading ? undefined : handleDrop}
        on:click={isUploading ? undefined : triggerFileInput}
        on:keydown={isUploading ? undefined : handleKeyDown}
        aria-label="Zone de dépôt d'image"
        aria-disabled={isUploading}
    >
        {#if isUploading}
            <p class="mt-xs text-sm text-content-quiet">
                {trans('form.multi_image_upload.sending')}
            </p>
        {:else}
            <div class="text-sm">
                {trans('form.multi_image_upload.drop_image')}
            </div>
            <div class="my-xs text-sm">ou</div>
            <Button
                type="button"
                intent="tertiary"
                size="small"
                disabled={isUploading}
                tabindex={-1}
                startIcon={DownloadIcon}
            >
                {trans('form.multi_image_upload.add')}
            </Button>
            <div class="mt-m text-sm">
                {trans('form.multi_image_upload.size_limit')}
            </div>
        {/if}
    </div>
    {#if isUploading && !uploadError}
        <p class="mt-xs text-sm text-content-quiet">
            {trans('form.multi_image_upload.sending')}
        </p>
    {/if}
    {#if uploadError}
        <p class="mt-xs text-sm text-status-negative">
            {trans('form.multi_image_upload.error')}
            {uploadError}
        </p>
    {/if}
</div>
